import Modal from "react-bootstrap/Modal";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as yup from "yup";

import { useAuth } from "../../hooks/useAuth";
import { fetchActionsUtil } from "../../utils/helpers";
import ConfirmDialog from "../ConfirmDialogue";
import ModalLoader from "../utils/ModalLoader";
import Select from "react-select";
import CloseIcon from "mdi-react/CloseIcon";
import AsyncCreatableSelect from "react-select/async-creatable";
import useDebounce from "../../utils/hooks";
import { debounce } from "lodash";

const CreateStorageLocationModal = (props) => {
  const { backendUrl } = useAuth();
  const queryClient = useQueryClient();
  const [creatingArea, setCreatingArea] = useState(false);

  const getWarehouses = async () => {
    const warehouses = await fetchActionsUtil(
      `${backendUrl}/api/warehouse`,
      "GET",
    );

    // console.log(warehouses);
    warehouses.warehouses = warehouses.warehouses.map((el) => ({
      value: el.W_ID,
      label: el.W_name,
    }));

    return warehouses;
  };

  const { data, isFetching } = useQuery(
    ["FETCH_WAREHOUSE", useQuery],
    () => getWarehouses(),
    {
      keepPreviousData: true,
    },
  );

  //   console.log(data);

  const saveStorageLocation = useMutation(
    (payload) =>
      fetchActionsUtil(
        `${backendUrl}/api/StorageLocation`,
        "POST",
        "",
        payload,
      ),
    {
      onSuccess: () => {
        toast.success("Storage Location saved Successfully");
        formik.resetForm({
          W_name: "",
          storage_location: "",
          storage_areas: [],
        });
        props?.refetch && props?.refetch();
        props?.onHide();
      },
      onError: () => {
        toast.error("Error!!!");
      },
    },
  );

  const editStorageLocation = useMutation(
    (payload) =>
      fetchActionsUtil(
        `${backendUrl}/api/StorageLocation/edit/${
          props?.pickedStorage?.Storage_ID
        }`,
        "POST",
        "",
        payload,
      ),
    {
      onSuccess: () => {
        toast.success("Storage Location updated Successfully");
        formik.resetForm({
          W_name: "",
          storage_location: "",
          storage_areas: [],
        });
        props?.refetch && props?.refetch();
        props?.onHide();
      },
      onError: ({ message }) => {
        toast.error(message ? message : "Error!!!");
      },
    },
  );

  const formik = useFormik({
    initialValues: {
      W_name: "",
      storage_location: "",
      storage_areas: [],
    },
    validationSchema: yup.object().shape({
      W_name: yup.string().required("Warehouse Name is required"),
      storage_location: yup.string().required("Storage Location is required"),
    }),
    onSubmit: async (values) => {
      if (
        await ConfirmDialog({
          title: `${props?.editStorage ? "Edit" : "Create"} Storage Location`,
          description: `Are you sure you want to ${
            props?.editStorage ? "Edit" : "Create"
          } this storage location `,
        })
      ) {
        // console.log(values);
        if (props?.editStorage) {
          editStorageLocation.mutate(values);
        } else {
          saveStorageLocation.mutate(values);
        }
      }
    },
  });

  useEffect(() => {
    formik.setFieldValue(
      "W_name",
      props?.StorageWarehouseName?.W_ID ? props.StorageWarehouseName?.W_ID : "",
    );

    if (props?.editStorage) {
      formik.setValues({
        W_name: props?.pickedStorage?.W_name,
        storage_location: props?.pickedStorage?.storage_location,
        storage_areas: (props?.pickedStorage?.storage_areas || []).map((a) => ({
          value: a.Storage_Area_ID ?? a.value,
          label: a.Name ?? a.label,
        })),
      });
    }
  }, []);

  // ---- Storage Areas: fetch + search ----
  const fetchStorageAreas = async (inputValue = "") => {
    try {
      const response = await fetchActionsUtil(
        `${backendUrl}/api/StorageLocation/storage-areas?name=${inputValue}`,
        "GET",
      );

      return (response?.storageAreas || []).map((el) => ({
        value: el.id,
        label: el.Name,
      }));
    } catch (err) {
      console.log(err);
      return [];
    }
  };

  // Backs defaultOptions (initial list shown before typing) — kept in
  // react-query cache so we can push newly-created options into it below.
  const { data: storageAreasData } = useQuery(
    ["STORAGE_AREAS_LIST"],
    () => fetchStorageAreas(),
    { keepPreviousData: true },
  );

  const debouncedFetchStorageAreas = debounce(async (inputValue, callback) => {
    const options = await fetchStorageAreas(inputValue);
    callback(options);
  }, 500);

  const loadStorageAreaOptions = (inputValue, callback) => {
    try {
      debouncedFetchStorageAreas(inputValue, callback);
    } catch (err) {
      console.log(err);
      callback([]);
    }
  };

  // ---- Storage Areas: create new ----
  const createStorageArea = useMutation(
    (name) =>
      fetchActionsUtil(
        `${backendUrl}/api/StorageLocation/storage-areas`,
        "POST",
        "",
        {
          Name: name,
        },
      ),
    {
      onError: ({ message }) => {
        toast.error(message || "Unable to create storage area");
      },
    },
  );

  const handleCreateStorageArea = async (inputValue) => {
    if (creatingArea) return;
    setCreatingArea(true);
    try {
      const res = await createStorageArea.mutateAsync(inputValue);

      const newOption = {
        value: res?.storageArea?.id,
        label: res?.storageArea?.Name ?? inputValue,
      };

      // Add the freshly created option to the current field selection
      formik.setFieldValue("storage_areas", [
        ...(formik.values.storage_areas || []),
        newOption,
      ]);

      // Also seed it into the cached list so it shows up immediately in
      // defaultOptions without waiting on a refetch.
      queryClient.setQueryData(["STORAGE_AREAS_LIST"], (old) => [
        ...(old || []),
        newOption,
      ]);

      toast.success("Storage area created");
    } catch (err) {
      console.log(err);
    } finally {
      setCreatingArea(false);
    }
  };

  return (
    <Modal
      onHide={props.onHide}
      show={props.show}
      size="md"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Body>
        <div>
          <div>
            <div className="d-flex justify-content-between">
              <h6>{props?.editStorage ? "Edit" : "Create"} Storage Location</h6>
              <div role="button" className="text-lg" onClick={props.onHide}>
                <CloseIcon />
              </div>
            </div>
            <p className="text-muted">
              {props?.editStorage ? "Edit" : "Create a new"} Storage Location
            </p>
          </div>
          <div className="mt-3">
            {/*  */}
            <div className="mb-3">
              <label className="col-form-label fw-bold">
                Storage Location Name
              </label>
              <div className="form-group">
                <input
                  type="text"
                  className="form-control"
                  value={formik.values.storage_location}
                  name="storage_location"
                  onChange={formik.handleChange}
                />
                <span className="form-control-feedback text-danger">
                  {formik.errors.storage_location}
                </span>
              </div>
            </div>
            {/*  */}
            <div className="mb-3">
              <label className="col-form-label fw-bold">Warehouse Name</label>
              <div className="form-group">
                <Select
                  classNamePrefix="form-select"
                  value={data?.warehouses?.find(
                    (d) => d.value === formik.values.W_name,
                  )}
                  options={data?.warehouses}
                  onChange={(selected) => {
                    formik.setFieldValue("W_name", selected.value);
                  }}
                />

                <span className="form-control-feedback text-danger">
                  {formik.errors.W_name}
                </span>
              </div>
            </div>

            {/* Storage Areas */}
            <div className="mb-3">
              <label className="col-form-label fw-bold">Storage Areas</label>
              <div className="form-group">
                <AsyncCreatableSelect
                  classNamePrefix="form-select"
                  isMulti
                  cacheOptions
                  defaultOptions={storageAreasData || []}
                  loadOptions={loadStorageAreaOptions}
                  value={formik.values.storage_areas}
                  onChange={(selected) => {
                    console.log("=== ", selected);
                    formik.setFieldValue("storage_areas", selected || []);
                  }}
                  onCreateOption={handleCreateStorageArea}
                  isDisabled={creatingArea}
                  isLoading={creatingArea}
                  getOptionValue={(opt) => opt.value}
                  getOptionLabel={(opt) => opt.label}
                  formatCreateLabel={(inputValue) =>
                    `Create new storage area: "${inputValue}"`
                  }
                  loadingMessage={() => "Loading storage areas…"}
                  noOptionsMessage={({ inputValue }) =>
                    inputValue
                      ? `No storage area found for "${inputValue}"`
                      : "No storage areas"
                  }
                  placeholder="Select or create storage area(s)..."
                />
                <span className="form-control-feedback text-danger">
                  {formik.errors.storage_areas}
                </span>
              </div>
            </div>

            {/*  */}
          </div>
        </div>
      </Modal.Body>
      <div className="d-inline-flex justify-content-end m-4">
        <button className="btn bg print mx-4" onClick={props.onHide}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={formik.handleSubmit}>
          {props?.editStorage ? "Edit" : "Save"} Storage Location
        </button>
      </div>
      <ModalLoader show={isFetching || saveStorageLocation.isLoading} />
    </Modal>
  );
};

export default CreateStorageLocationModal;
